// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Rights request types — PDPL / Decree 356 canonical vocabulary.
 *
 * Sprint 43.5.2 unified the RequestType values with PdplRightType
 * (src/shared/src/types/dsr-fulfillment.ts) so the platform speaks a single
 * vocabulary across cases, response templates, seed packs, audit records,
 * and UI. The Phase-1 GDPR-flavoured keys `DSAR`/`RECTIFICATION` were
 * renamed to `ACCESS`/`CORRECTION` and a new `CONSENT_WITHDRAWAL` entry was
 * added to round out the PDPL right set.
 *
 * See: docs/backend/data-model.md, docs/product/phases/phase-1/sprint-1.0.0.md,
 *      docs/compliance/vn-regulator-forms/README.md (PDPL rights spec).
 */
export const RequestType = {
    ACCESS: 'access',
    DELETION: 'deletion',
    CORRECTION: 'correction',
    OBJECTION: 'objection',
    PORTABILITY: 'portability',
    RESTRICTION: 'restriction',
    CONSENT_WITHDRAWAL: 'consent_withdrawal',
    PROTECTIVE_MEASURES: 'protective_measures',
    /*
     * Sprint 63.31.0 (#438, owner ruling OD-B) — three rights a data subject holds
     * under Law 91 Điều 4 khoản 1 that the vocabulary did not name (điểm a, the
     * first half of điểm b, and điểm đ). Like `portability`, each is OFFERED only
     * where an installed framework declares it (`data_subject_request_types` on
     * the framework record, R-46); the vocabulary carries the word, the pack says
     * whether the right exists. No installed clock dates any of them, so a case is
     * dated as Q1/Q2 rule (tenant target, else the platform default) — except
     * `complaint_or_compensation`, which is handled in Legal Matters and dated by
     * nothing (owner ruling OD-E; {@link REQUEST_TYPES_HANDLED_IN_LEGAL_MATTERS}).
     */
    PROCESSING_INFORMATION: 'processing_information',
    CONSENT_DECISION: 'consent_decision',
    COMPLAINT_OR_COMPENSATION: 'complaint_or_compensation',
};
/** All valid request type values. */
export const ALL_REQUEST_TYPES = Object.values(RequestType);
/**
 * Request type labels for bilingual UI.
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - A.3 Request Types
 */
export const REQUEST_TYPE_LABELS = {
    [RequestType.ACCESS]: {
        en: 'Access (DSAR)',
        vi: 'Yeu cau truy cap du lieu',
    },
    [RequestType.DELETION]: {
        en: 'Deletion',
        vi: 'Yeu cau xoa du lieu',
    },
    [RequestType.CORRECTION]: {
        en: 'Correction / Update',
        vi: 'Yeu cau chinh sua du lieu',
    },
    [RequestType.OBJECTION]: {
        en: 'Objection',
        vi: 'Phan doi xu ly',
    },
    [RequestType.PORTABILITY]: {
        en: 'Data Portability',
        vi: 'Yeu cau chuyen du lieu',
    },
    [RequestType.RESTRICTION]: {
        en: 'Restriction of Processing',
        vi: 'Han che xu ly du lieu',
    },
    [RequestType.CONSENT_WITHDRAWAL]: {
        en: 'Consent Withdrawal',
        vi: 'Rut lai su dong y',
    },
    [RequestType.PROTECTIVE_MEASURES]: {
        en: 'Request personal data protection measures and solutions',
        vi: 'Yêu cầu thực hiện các biện pháp, giải pháp bảo vệ dữ liệu cá nhân',
    },
    [RequestType.PROCESSING_INFORMATION]: {
        en: 'Be informed about the processing of personal data',
        vi: 'Được biết về hoạt động xử lý dữ liệu cá nhân',
    },
    [RequestType.CONSENT_DECISION]: {
        en: 'Consent or refuse consent to processing',
        vi: 'Đồng ý hoặc không đồng ý cho phép xử lý dữ liệu cá nhân',
    },
    [RequestType.COMPLAINT_OR_COMPENSATION]: {
        en: 'Complaint, denunciation, lawsuit or claim for damages',
        vi: 'Khiếu nại, tố cáo, khởi kiện, yêu cầu bồi thường thiệt hại',
    },
};
/**
 * Sprint 63.31.0 (#438, owner ruling OD-B) — the request types offered ONLY
 * where an installed framework declares them.
 *
 * An installed framework declares the request types its instrument grants in
 * `data_subject_request_types` (read by `rights-requests/request-type-offering.ts`).
 * Where at least one operating framework declares the list, the offered set is
 * exactly their union. Where none does (a tenant with no rights content, or
 * content that predates the declaration), the platform offers its vocabulary
 * less these four: each is a right that exists only because a particular
 * instrument grants it, so offering it with no instrument installed would be
 * the platform deciding that a right exists. The owner named them (OD-B):
 * `portability`, and the three Law 91 Điều 4 khoản 1 rights added in 63.31.0.
 * No instrument is named here (R-46); the list is a property of the words.
 */
export const REQUEST_TYPES_OFFERED_ONLY_WHERE_DECLARED = [
    RequestType.PORTABILITY,
    RequestType.PROCESSING_INFORMATION,
    RequestType.CONSENT_DECISION,
    RequestType.COMPLAINT_OR_COMPENSATION,
];
/**
 * Sprint 63.31.0 (gate E12.5 pass 1, owner ruling OD-E) — the request types a
 * data subject may choose at intake that are NOT worked as rights requests.
 *
 * An intake of one of these opens (or joins) a legal matter with a
 * statutory-right origin in the Legal Matters register, which already receives
 * complaints and denunciations under the same provision. The case it leaves is
 * a thin intake record pointing at the matter: no clock, no organisation
 * target and no platform default dates it, the SLA monitor and the
 * rights-deadline sweep never act on it, and nothing shows it as overdue.
 * The legal matter is the one record a person works.
 *
 * The list is a property of the words, not of an instrument (R-46): which
 * right the matter rests on is read from the installed frameworks
 * (`rights-requests/legal-matter-routing.ts`).
 */
export const REQUEST_TYPES_HANDLED_IN_LEGAL_MATTERS = [
    RequestType.COMPLAINT_OR_COMPENSATION,
];
/** Whether an intake of this request type is handled in Legal Matters (OD-E). */
export function isRequestTypeHandledInLegalMatters(requestType) {
    return REQUEST_TYPES_HANDLED_IN_LEGAL_MATTERS.includes(requestType);
}
/**
 * Sprint 63.31.0 (#422, owner ruling Q2 (b), decision E6) — the PLATFORM's
 * working default for a rights request's fulfilment date, NOT a legal period.
 *
 * It dates a case only when no installed clock in force dates it AND the
 * organisation has saved no target for the request type (a configuration never
 * saved, or a saved table that omits the type). A case dated from it records
 * `clock_source: 'platform_default'`, and every screen names it as *platform
 * default, not a legal period*. These are the figures the SLA service carried
 * as `DEFAULT_DEADLINES` and a literal 30 (calendar days; 45 for deletion),
 * moved here so there is one declaration and it carries its own label.
 */
export const PLATFORM_DEFAULT_RIGHTS_PERIODS = Object.freeze({
    unit: 'calendar_days',
    /** Every request type not named in `by_request_type`. */
    default_days: 30,
    by_request_type: Object.freeze({ [RequestType.DELETION]: 45 }),
});
/** The platform default (calendar days) for one request type — see {@link PLATFORM_DEFAULT_RIGHTS_PERIODS}. */
export function platformDefaultRightsPeriodDays(requestType) {
    const named = PLATFORM_DEFAULT_RIGHTS_PERIODS.by_request_type[requestType];
    if (typeof named === 'number')
        return named;
    return PLATFORM_DEFAULT_RIGHTS_PERIODS.default_days;
}
//# sourceMappingURL=request-types.js.map